<template>
  <div>
    <v-speed-dial
      v-model="speedDial"
      fixed
      bottom
      left
      direction="top"
      transition="slide-y-reverse-transition"
    >
      <v-btn
        slot="activator"
        v-model="speedDial"
        color="primary"
        dark
        fab
      >
        <v-icon v-html="selectedDisplayIcon"></v-icon>
        <v-icon>close</v-icon>
      </v-btn>

      <v-btn
        fab
        dark
        small
        :color="mode.modeId === currentMode ? 'accent' : 'primary'"
        @click="switchToMode(mode)"
        v-for="mode in displayModes"
        :key="mode.modeId"
      >
        <v-icon v-html="mode.icon"></v-icon>
      </v-btn>

      <v-tooltip right v-if="previewUrl">
        <v-fab-transition slot="activator">
          <v-btn
            fab
            small
            color="primary"
            @click="openDialog">
            <v-icon>devices</v-icon>
          </v-btn>
        </v-fab-transition>
        <span v-html="$t('message.editor.preview.openQR')"></span>
      </v-tooltip>

      <v-tooltip right v-if="previewUrl">
        <v-fab-transition slot="activator">
          <v-btn
            :href="previewUrl"
            fab
            small
            color="primary"
            target="_blank">
            <v-icon>launch</v-icon>
          </v-btn>
        </v-fab-transition>
        <span v-html="$t('message.editor.preview.openNewTab')"></span>
      </v-tooltip>

    </v-speed-dial>
    <v-speed-dial
      v-if="showAdditionalModes"
      v-model="additionalModesDial"
      fixed
      bottom
      style="left: 88px"
      direction="top"
      transition="slide-y-reverse-transition"
    >
      <v-btn
        slot="activator"
        v-model="additionalModesDial"
        color="primary"
        dark
        fab
      >
        <v-icon>settings</v-icon>
        <v-icon>close</v-icon>
      </v-btn>

      <component
        v-for="mode in additionalModes"
        :key="mode.name"
        :active.sync="mode.active"
        :disabled="mode.disabled"
        :is="mode.component"
      />

    </v-speed-dial>

    <v-dialog v-model="showDialog" max-width="600px" @keydown.esc="showDialog = false">
      <v-card>
        <v-card-title>
          <span class="headline" v-html="$t('message.editor.preview.previewOnAnotherDevice')"></span>
        </v-card-title>
        <v-card-text>

          <v-layout>
            <v-flex xs12 align-center justify-center>
              <p class="body-1"
                 v-html="$t(`message.editor.preview.QR.${type}`)">
              </p>
              <p class="qr-container text-xs-center mt-4">
                <img
                  v-if="qrUrl"
                  :src="qrUrl"
                  :alt="$t('message.editor.preview.anotherDevice')">
              </p>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="accent" flat @click.stop="showDialog = false">Cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <nx-dialog-confirm
      :text="$t('message.editor.preview.unsavedChanges.text')"
      :title="$t('message.editor.preview.unsavedChanges.title')"
      accept-color="success"
      cancel-color="error"
      ref="confirm"
    />
  </div>
</template>

<script>
import $http from '@/api/without-catches';
import NxDialogConfirm from '@/components/dialogs/Confirm';

/**
 * @emits input(displayMode)
 */
export default {
  name: 'nx-display-mode-selector',
  components: {
    NxDialogConfirm,
  },
  props: {
    currentMode: {
      type: Number,
      required: true,
    },
    previewUrl: {
      type: String,
    },
    displayModes: {
      type: [Object, Array],
    },
    additionalModes: {
      type: Array,
    },
    type: {
      type: String,
      validator: v => ['content', 'board'].includes(v),
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      speedDial: false,
      showDialog: false,
      qrUrl: null,
      additionalModesDial: false,
      currentAdditionalMode: null,
    };
  },
  computed: {
    selectedDisplayIcon() {
      return this.displayModes[this.currentMode].icon;
    },
    showAdditionalModes() {
      if (!this.additionalModes) {
        return false;
      }
      return !!this.additionalModes.length;
    },
  },
  watch: {
    previewUrl() {
      this.qrUrl = null;
    },
  },
  methods: {
    async openDialog() {
      this.showDialog = true;
      this.qrUrl = await this.generateQrUrl(this.previewUrl);
    },
    async generateQrUrl(url) {
      // Fist generate the url shortener endpoint
      const shortUrlEndpoint = `http://tinyurl.com/api-create.php?url=${encodeURIComponent(url)}`;
      // Get the shortUrl
      let shortUrl = url;
      try {
        shortUrl = await $http.get(`/api/proxy?url=${encodeURI(shortUrlEndpoint)}`);
      } catch (e) {
        console.warn('Error trying to minify the preview URL. Using the long one instead.');
      }
      // Return a qr
      return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=0&data=${encodeURIComponent(shortUrl)}`;
    },
    async switchToMode(mode) {
      // If editor is dirty and mode has query params user must confirm action,
      // because preview iframe will be reloaded and draft data will be lost
      let shouldEmit = true;
      if (this.dirty && mode.additionalPreviewQueryParams) {
        shouldEmit = await this.$refs.confirm.open();
      }

      if (shouldEmit) {
        this.$emit('input', mode.modeId);
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.qr-container {
  min-height: 200px;
}
</style>
